import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * The forced first-time change.
 *
 * `app_user.must_change_password` is true while the current password is one
 * somebody else chose and therefore knows. Until it is false the session may
 * do exactly one thing, and the server is what enforces that -- every route
 * except this one and /me depends on `auth.active_user`.
 *
 * So this screen is not a gate. It is the only thing the gate lets through.
 */
export default function ChangePassword() {
  const { user, refresh, signOut } = useAuth();
  const navigate = useNavigate();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const forced = user?.must_change_password ?? false;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (next !== confirm) {
      setError("The two new passwords do not match.");
      return;
    }
    if (next.length < 12) {
      setError("Password must be at least 12 characters.");
      return;
    }
    if (next === current) {
      setError("The new password must differ from the current one.");
      return;
    }

    setBusy(true);
    try {
      await api.changePassword(current, next);
      // Changing a password revokes every other session for this user, so the
      // shell has to be re-resolved rather than assumed.
      await refresh();
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.detail
          : "Could not reach the server. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <form className="auth-card" onSubmit={onSubmit}>
        <h1>{forced ? "Choose your own password" : "Change your password"}</h1>
        <p className="lede">
          {forced
            ? "Someone else knows the password you were given. Change it to continue."
            : "Signs out your other sessions."}
        </p>

        {error ? (
          <div className="form-error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="field">
          <label htmlFor="current">Current password</label>
          <input
            id="current"
            type="password"
            autoComplete="current-password"
            autoFocus
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="next">New password</label>
          <input
            id="next"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <div className="form-hint">At least 12 characters.</div>
        </div>

        <div className="field">
          <label htmlFor="confirm">New password again</label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        <button className="primary" type="submit" disabled={busy}>
          {busy ? "Saving…" : "Set password"}
        </button>

        <button
          type="button"
          className="link"
          style={{ marginTop: 14 }}
          onClick={() => {
            void signOut().then(() => navigate("/login", { replace: true }));
          }}
        >
          Sign out instead
        </button>
      </form>
    </div>
  );
}
